<div class="care-networks">
	<!-- Loading state -->
	@if (loading) {
	<div class="flex items-center justify-center py-12">
		<div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
		<span class="ml-3 text-gray-600">Chargement des réseaux de soins...</span>
	</div>
	}

	<!-- Content -->
	@if (!loading && !error) {
	<!-- Filters and Search -->
	<div class="mb-6 space-y-4">
		<!-- Type filters -->
		<div class="flex flex-wrap gap-2">
			<button (click)="selectedFilter = 'professional'; onFilterChange()"
				[class.bg-primary]="selectedFilter === 'professional'"
				[class.text-white]="selectedFilter === 'professional'"
				[class.bg-gray-100]="selectedFilter !== 'professional'"
				[class.text-gray-700]="selectedFilter !== 'professional'"
				class="px-3 py-1 rounded-full text-sm font-medium transition-colors">
				👨‍⚕️ Professionnels
			</button>
			<button (click)="selectedFilter = 'structure'; onFilterChange()"
				[class.bg-primary]="selectedFilter === 'structure'" [class.text-white]="selectedFilter === 'structure'"
				[class.bg-gray-100]="selectedFilter !== 'structure'"
				[class.text-gray-700]="selectedFilter !== 'structure'"
				class="px-3 py-1 rounded-full text-sm font-medium transition-colors">
				🏥 Structures
			</button>
			<button (click)="selectedFilter = 'pharmacy'; onFilterChange()"
				[class.bg-primary]="selectedFilter === 'pharmacy'" [class.text-white]="selectedFilter === 'pharmacy'"
				[class.bg-gray-100]="selectedFilter !== 'pharmacy'"
				[class.text-gray-700]="selectedFilter !== 'pharmacy'"
				class="px-3 py-1 rounded-full text-sm font-medium transition-colors">
				💊 Pharmacies
			</button>
		</div>

		<!-- Search -->
		<div class="relative">
			<input type="text" [(ngModel)]="searchTerm" [placeholder]="getSearchPlaceholder()"
				(input)="onSearchChange()"
				class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm">
			<svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor"
				viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
					d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
			</svg>
			@if (searchLoading) {
			<div class="absolute right-3 top-2.5">
				<div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
			</div>
			}
		</div>

		@if (searchError) {
		<div class="text-red-500 text-sm mt-2">{{ searchError }}</div>
		}
	</div>

	<!-- Networks list -->
	@if (searchResults.length === 0) {
	<div class="text-center py-12">
		<div class="text-gray-400 text-[32px] mb-4">{{ getTypeIcon(selectedFilter) }}</div>
		<h3 class="text-lg font-medium text-gray-900 mb-2">{{ getEmptyMessage() }}</h3>
		<p class="text-gray-500">Aucun {{ getTypeLabel(selectedFilter).toLowerCase() }} ne correspond à vos critères de
			recherche.</p>
	</div>
	} @else {
	<!-- Affichage unifié avec cartes simples -->
	<div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
		@if (selectedFilter === 'professional') {
		<!-- Cartes pour les professionnels -->
		@for (item of searchResults; track item.id) {
		<div class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
			(click)="onDoctorClick(item)">
			<!-- Header avec avatar -->
			<div class="flex items-center gap-3 mb-3">
				<div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
					@if (item.avatar) {
					<img [src]="item.avatar" [alt]="item.firstname" class="w-full h-full object-cover rounded-full">
					} @else {
					<span class="text-primary font-semibold text-lg">{{ item.firstname?.charAt(0) }}{{
						item.lastname?.charAt(0)
						}}</span>
					}
				</div>
				<div class="flex-1 min-w-0">
					<h3 class="font-semibold text-gray-900 !text-sm leading-tight">
						{{ item.title?.name }} {{ item.firstname }} {{ item.lastname }}
					</h3>
					@if (item.structures && item.structures.length > 0) {
					<p class="text-xs text-primary mt-1">{{ item.structures[0].name }}</p>
					}
				</div>
			</div>

			<!-- Spécialisations -->
			@if (item.specialisations && item.specialisations.length > 0) {
			<div class="mb-3">
				<div class="flex flex-wrap gap-1">
					@for (specialisation of item.specialisations.slice(0, 2); track specialisation.id) {
					<span class="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">{{ specialisation.name }}</span>
					}
					@if (item.specialisations.length > 2) {
					<span class="text-xs text-gray-500">+{{ item.specialisations.length - 2 }} autres</span>
					}
				</div>
			</div>
			}

			<!-- Adresse -->
			@if (item.coordonate?.globalAddressField) {
			<div class="flex items-start gap-2 mb-2">
				<lucide-icon [img]="MapPin" class="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"></lucide-icon>
				<p class="text-xs text-gray-600 line-clamp-2">{{ item.coordonate.globalAddressField }}</p>
			</div>
			}

			<!-- Contact -->
			@if (hasContactInfo(item)) {
			<div class="flex items-center gap-2 mb-3">
				<lucide-icon [img]="Phone" class="w-4 h-4 text-gray-400 flex-shrink-0"></lucide-icon>
				<span class="text-xs text-gray-600">
					{{ getDisplayPhoneNumber(item) }}
				</span>
			</div>
			}

			<!-- Actions -->
			<div class="flex items-center justify-end pt-3 border-t border-gray-100">
				<button
					class="text-xs text-primary hover:text-primary/80 font-medium px-3 py-1 rounded border border-primary/20 hover:bg-primary/5 transition-colors"
					(click)="onDoctorClick(item); $event.stopPropagation()">
					Voir détails
				</button>
			</div>
		</div>
		}
		} @else {
		<!-- Affichage des structures et pharmacies -->
		@for (item of searchResults; track item.id) {
		<div (click)="onNetworkClick(item)"
			class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
			<!-- Header -->
			<div class="flex items-start justify-between mb-3">
				<div class="flex items-start gap-2">
					<span class="text-2xl">{{ getTypeIcon(selectedFilter) }}</span>
					<div class="flex-1">
						<h3 class="font-semibold text-gray-900 !text-sm">{{ item.name }}</h3>
						<span class="text-xs text-gray-500">{{ item.structureType?.name || getTypeLabel(selectedFilter)
							}}</span>
						<!-- Prestataire Connecté indicator -->
						@if ((selectedFilter === 'structure' || selectedFilter === 'pharmacy') && item.eyoneUser) {
						<div class="flex items-center gap-1 mt-1"
							[title]="'Ce prestataire échange automatiquement vos informations avec votre {{ getProductName() }} pour faciliter votre prise en charge.'">
							<span
								class="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap">
								<lucide-icon [img]="CircleIcon" [size]="10"
									class="fill-green-500 text-green-500"></lucide-icon>
								Prestataire Connecté
							</span>
						</div>
						}
					</div>
				</div>
				@if (item.enabled) {
				<span class="bg-green-100 text-green-600 text-xs px-2 py-1 rounded-full">Actif</span>
				}
			</div>

			<!-- Address -->
			@if (item.coordonate?.globalAddressField) {
			<div class="flex items-start gap-2 mb-3">
				<span class="w-4 h-4 flex items-center justify-center">
					<lucide-icon [img]="MapPin" class="w-4 h-4 text-gray-400"></lucide-icon>
				</span>
				<p class="text-xs text-gray-600 line-clamp-2 flex-1">{{ item.coordonate.globalAddressField }}</p>
			</div>
			}

			<!-- Contact -->
			@if (hasContactInfo(item)) {
			<div class="flex items-start gap-2 mb-3">
				<span class="w-4 h-4 flex items-center justify-center">
					<lucide-icon [img]="Phone" class="w-4 h-4 text-gray-400"></lucide-icon>
				</span>
				<span class="text-xs text-gray-600 flex-1">
					{{ getDisplayPhoneNumber(item) }}
				</span>
			</div>
			}
			@if (item.description) {
			<div class="mb-3">
				<p class="text-xs text-gray-600 line-clamp-2">{{ item.description }}</p>
			</div>
			}

			<!-- Languages -->
			@if (item.languages && item.languages.length > 0) {
			<div class="mb-3">
				<div class="flex flex-wrap gap-1">
					@for (language of item.languages.slice(0, 2); track language.id) {
					<span class="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">{{ language.name }}</span>
					}
					@if (item.languages.length > 2) {
					<span class="text-xs text-gray-500">+{{ item.languages.length - 2 }} autres</span>
					}
				</div>
			</div>
			}

			<!-- Specialisations -->
			@if (item.specialisations && item.specialisations.length > 0) {
			<div class="mb-3">
				<div class="flex flex-wrap gap-1">
					@for (specialisation of item.specialisations.slice(0, 2); track specialisation.id) {
					<span class="bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded">{{ specialisation.name
						}}</span>
					}
					@if (item.specialisations.length > 2) {
					<span class="text-xs text-gray-500">+{{ item.specialisations.length - 2 }} autres</span>
					}
				</div>
			</div>
			}

			<!-- Actions -->
			<div class="flex items-center justify-end pt-3 border-t border-gray-100">
				<button
					class="text-xs text-primary hover:text-primary/80 font-medium px-3 py-1 rounded border border-primary/20 hover:bg-primary/5 transition-colors"
					(click)="onNetworkClick(item); $event.stopPropagation()">
					Voir détails
				</button>
			</div>
		</div>
		}
		}
	</div>
	}
	}
</div>